"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode
} from "react";
import type { InfiniteHitsCache } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import {
	Configure,
	InstantSearch,
	useClearRefinements,
	useInfiniteHits,
	useInstantSearch,
	useRange,
	useRefinementList,
	useSortBy,
	useToggleRefinement
} from "react-instantsearch";

import { searchCategoryListing } from "@/app/actions/category-listing";
import type { ProductCardViewModel } from "@/app/components/shared/product-card";
import { ProductCardWithCart } from "@/app/components/shared/product-card/ProductCardWithCart";
import {
	FilterChips,
	FilterPanel,
	ListingMessage,
	LoadMore,
	RANGE_VALUE,
	SortControl,
	selectAppliedFilters,
	type FilterGroup,
	type FilterPanelPresentation,
	type FilterRange,
	type ListingMessageProps
} from "@/composites/product-listing";
import { ProductGrid } from "@/composites/product";
import {
	LISTING_PAGE_PARAM,
	LISTING_PAGE_SIZE,
	MAX_LISTING_PAGES
} from "@/domain/product-listing/listing-depth";
import {
	LISTING_TOGGLE_VALUES,
	type ListingFacetAttribute
} from "@/domain/product-listing/listing-facets";

import type { ListingFilterDefinition } from "../category-content";
import type { CategoryListing } from "../get-category-page-data";
import {
	NO_SELECTION,
	toFilterGroups,
	type FacetCounts,
	type FacetSelection,
	type FacetStats,
	type RangeFormat
} from "../listing-filters";
import {
	createListingRouting,
	createListingSearchClient,
	LISTING_FACET_LIMIT,
	LISTING_INDEX,
	LISTING_SORT_OPTIONS
} from "../listing-search";
import { createListingHitsCache, type ServerPages } from "./listing-hits-cache";
import {
	hasRefinements,
	listingStatus,
	type ListingStatus
} from "./listing-status";

export interface ListingOnward {
	label: string;
	href: string;
}

type ListingHit = ProductCardViewModel & { objectID: string };

interface ListingStateValue {
	groups: FilterGroup[];
	cards: ProductCardViewModel[];
	total: number;
	isPending: boolean;
	sort: string;
	toggle: (groupId: string, value: string) => void;
	setRange: (groupId: string, range: FilterRange) => void;
	setSort: (sort: string) => void;
	clearAll: () => void;
	loadMore?: () => void;
	isLoadingMore: boolean;
	firstNewCard?: number;
	newCardFocused: () => void;
	status: ListingStatus;
	retry: () => void;
	onward: ListingOnward;
}

const ListingStateContext = createContext<ListingStateValue | null>(null);

function useListingState(): ListingStateValue {
	const value = useContext(ListingStateContext);
	if (!value) {
		throw new Error(
			"Listing controls must sit inside ListingStateProvider"
		);
	}
	return value;
}

const CURRENCY_BY_LOCALE: Record<string, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

function toRangeFormat(locale: string): RangeFormat {
	return {
		locale,
		formatOptions: {
			style: "currency",
			currency: CURRENCY_BY_LOCALE[locale] ?? "USD",
			maximumFractionDigits: 0
		}
	};
}

export interface ListingStateProviderProps {
	superCategoryCode: string;
	categoryCode: string;
	locale: string;
	initial: CategoryListing;
	onward: ListingOnward;
	children: ReactNode;
}

export function ListingStateProvider({
	superCategoryCode,
	categoryCode,
	locale,
	initial,
	onward,
	children
}: ListingStateProviderProps): JSX.Element {
	const searchClient = useMemo(
		() =>
			createListingSearchClient((searches) =>
				searchCategoryListing({
					superCategoryCode,
					categoryCode,
					locale,
					searches
				})
			),
		[superCategoryCode, categoryCode, locale]
	);
	const attributes = useMemo(
		() => [...new Set(initial.filters.map((filter) => filter.attribute))],
		[initial.filters]
	);
	const routing = useMemo(
		() => createListingRouting(attributes),
		[attributes]
	);
	const rangeFormat = useMemo(() => toRangeFormat(locale), [locale]);
	const cache = useMemo(
		() =>
			createListingHitsCache<ListingHit>(
				`plp-listing:${locale}:${categoryCode}`,
				toServerPages(initial)
			),
		[locale, categoryCode, initial]
	);
	// InstantSearch renders nothing before it starts, so show the server's page.
	const [searchStarted, setSearchStarted] = useState(false);
	const markStarted = useCallback(() => setSearchStarted(true), []);

	return (
		<>
			<InstantSearch
				searchClient={searchClient}
				indexName={LISTING_INDEX}
				routing={routing}
				future={{ preserveSharedStateOnUnmount: true }}
			>
				<Configure hitsPerPage={LISTING_PAGE_SIZE} />
				<ListingWidgets filters={initial.filters} />
				<ListingFacets
					initial={initial}
					onward={onward}
					cache={cache}
					rangeFormat={rangeFormat}
					onStart={markStarted}
				>
					{children}
				</ListingFacets>
			</InstantSearch>
			{!searchStarted && (
				<ListingStateContext.Provider
					value={toServerState(initial, rangeFormat, onward)}
				>
					{children}
				</ListingStateContext.Provider>
			)}
		</>
	);
}

function noop(): void {}

function toServerPages(initial: CategoryListing): ServerPages<ListingHit> {
	const hits = initial.cards.map((card, index) => ({
		...card,
		objectID: card.product.sku,
		__position: index + 1
	}));
	const pages: (typeof hits)[] = [];
	for (let start = 0; start < hits.length; start += LISTING_PAGE_SIZE) {
		pages.push(hits.slice(start, start + LISTING_PAGE_SIZE));
	}
	return { pages, complete: hits.length >= initial.total };
}

function toServerState(
	initial: CategoryListing,
	rangeFormat: RangeFormat,
	onward: ListingOnward
): ListingStateValue {
	return {
		groups: toFilterGroups(
			initial.filters,
			{
				total: initial.total,
				counts: initial.facets,
				stats: initial.stats
			},
			NO_SELECTION,
			rangeFormat
		),
		cards: initial.cards,
		total: initial.total,
		isPending: false,
		sort: LISTING_INDEX,
		toggle: noop,
		setRange: noop,
		setSort: noop,
		clearAll: noop,
		isLoadingMore: false,
		newCardFocused: noop,
		status: listingStatus({
			count: initial.cards.length,
			filtered: false,
			failed: false
		}),
		retry: noop,
		onward
	};
}

function ListingWidgets({
	filters
}: {
	filters: ListingFilterDefinition[];
}): JSX.Element {
	const attributes = new Map(
		filters.map((filter) => [filter.attribute, filter.kind])
	);
	return (
		<>
			{[...attributes].map(([attribute, kind]) => {
				switch (kind) {
					case "list":
						return (
							<ListWidget key={attribute} attribute={attribute} />
						);
					case "toggle":
						return (
							<ToggleWidget
								key={attribute}
								attribute={attribute}
							/>
						);
					case "range":
						return (
							<RangeWidget
								key={attribute}
								attribute={attribute}
							/>
						);
				}
			})}
			<SortWidget />
		</>
	);
}

interface WidgetProps {
	attribute: ListingFacetAttribute;
}

function ListWidget({ attribute }: WidgetProps): null {
	useRefinementList({ attribute, limit: LISTING_FACET_LIMIT });
	return null;
}

function ToggleWidget({ attribute }: WidgetProps): null {
	useToggleRefinement({
		attribute,
		on: [...(LISTING_TOGGLE_VALUES[attribute] ?? [])]
	});
	return null;
}

function RangeWidget({ attribute }: WidgetProps): null {
	useRange({ attribute });
	return null;
}

function SortWidget(): null {
	useSortBy({ items: LISTING_SORT_OPTIONS });
	return null;
}

function parseRange(value: string | undefined): FilterRange {
	const [min, max] = (value ?? "")
		.split(":")
		.map((part) => (part === "" ? undefined : Number(part)));
	return {
		min: Number.isFinite(min) ? min : undefined,
		max: Number.isFinite(max) ? max : undefined
	};
}

function ListingFacets({
	initial,
	onward,
	cache,
	rangeFormat,
	onStart,
	children
}: {
	initial: CategoryListing;
	onward: ListingOnward;
	cache: InfiniteHitsCache<ListingHit>;
	rangeFormat: RangeFormat;
	onStart: () => void;
	children: ReactNode;
}): JSX.Element {
	useLayoutEffect(onStart, [onStart]);

	const {
		items: hits,
		showMore,
		isFirstPage
	} = useInfiniteHits<ListingHit>({ cache });
	const { refine: clearAll } = useClearRefinements();
	const { results, status, indexUiState, setIndexUiState, refresh } =
		useInstantSearch({ catchError: true });
	const hasResults = !results.__isArtificial;
	const filtered = hasRefinements(indexUiState);
	// A failed refresh of the server's own page keeps its products; a failed filter or sort shows the error.
	const failed = status === "error" && (hasResults || filtered);
	const isPending = status === "loading" || status === "stalled";
	// Nothing cached reaches this url's depth, so it lists from the top instead of mid-way.
	const startsMidway = hasResults && !isFirstPage;
	useEffect(() => {
		if (startsMidway) {
			setIndexUiState((state) => ({ ...state, page: undefined }));
		}
	}, [startsMidway, setIndexUiState]);

	const [loadMoreFrom, setLoadMoreFrom] = useState<number>();
	const loadMore = useCallback(() => {
		setLoadMoreFrom(hits.length);
		showMore();
	}, [hits.length, showMore]);
	const newCardFocused = useCallback(() => setLoadMoreFrom(undefined), []);

	const counts: FacetCounts = {};
	const stats: FacetStats = {};
	for (const filter of initial.filters) {
		const values = results.getFacetValues(filter.attribute, {}) as
			{ name: string; count: number }[] | undefined;
		counts[filter.attribute] = Object.fromEntries(
			(Array.isArray(values) ? values : []).map((value) => [
				value.name,
				value.count
			])
		);
		const facetStats = results.getFacetStats(filter.attribute);
		if (facetStats) stats[filter.attribute] = facetStats;
	}

	const selection: FacetSelection = {
		lists: indexUiState.refinementList ?? {},
		toggles: indexUiState.toggle ?? {},
		ranges: Object.fromEntries(
			Object.entries(indexUiState.range ?? {}).map(
				([attribute, value]) => [attribute, parseRange(value)]
			)
		)
	};

	function filterById(groupId: string): ListingFilterDefinition | undefined {
		return initial.filters.find((filter) => filter.id === groupId);
	}

	function toggle(groupId: string, value: string): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const { attribute } = filter;
		setIndexUiState((state) => {
			if (filter.kind === "toggle") {
				return {
					...state,
					page: undefined,
					toggle: {
						...state.toggle,
						[attribute]: !state.toggle?.[attribute]
					}
				};
			}
			if (filter.kind === "range" && value === RANGE_VALUE) {
				return {
					...state,
					page: undefined,
					range: { ...state.range, [attribute]: "" }
				};
			}
			const current = state.refinementList?.[attribute] ?? [];
			const next = current.includes(value)
				? current.filter((entry) => entry !== value)
				: [...current, value];
			return {
				...state,
				page: undefined,
				refinementList: { ...state.refinementList, [attribute]: next }
			};
		});
	}

	function setRange(groupId: string, range: FilterRange): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const value = `${range.min ?? ""}:${range.max ?? ""}`;
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			range: {
				...state.range,
				[filter.attribute]: value === ":" ? "" : value
			}
		}));
	}

	function setSort(sort: string): void {
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			sortBy: sort
		}));
	}

	const handlers = {
		toggle,
		setRange,
		setSort,
		clearAll,
		loadMore,
		newCardFocused,
		retry: refresh,
		onward
	};
	const shown =
		hasResults && !startsMidway
			? {
					groups: toFilterGroups(
						initial.filters,
						{ total: results.nbHits, counts, stats },
						selection,
						rangeFormat
					),
					cards: hits,
					total: results.nbHits,
					isPending,
					sort: indexUiState.sortBy ?? LISTING_INDEX,
					...handlers,
					isLoadingMore: isPending && loadMoreFrom === hits.length,
					firstNewCard:
						loadMoreFrom !== undefined && hits.length > loadMoreFrom
							? loadMoreFrom
							: undefined
				}
			: {
					...toServerState(initial, rangeFormat, onward),
					...handlers,
					isPending: startsMidway
				};
	const value: ListingStateValue = {
		...shown,
		status: listingStatus({ count: shown.cards.length, filtered, failed })
	};

	return (
		<ListingStateContext.Provider value={value}>
			{children}
		</ListingStateContext.Provider>
	);
}

export function useListingTotal(): number {
	return useListingState().total;
}

export interface ListingFiltersProps {
	presentation: FilterPanelPresentation;
}

export function ListingFilters({
	presentation
}: ListingFiltersProps): JSX.Element | null {
	const { groups, toggle, setRange } = useListingState();

	return (
		<FilterPanel
			groups={groups}
			presentation={presentation}
			onValueChange={(change) => toggle(change.groupId, change.value)}
			onRangeChange={({ groupId, ...range }) => setRange(groupId, range)}
		/>
	);
}

export function ListingAppliedFilters(): JSX.Element | null {
	const { groups, toggle, clearAll } = useListingState();

	return (
		<FilterChips
			applied={selectAppliedFilters(groups)}
			onRemove={(filter) => toggle(filter.groupId, filter.value)}
			onClearAll={clearAll}
		/>
	);
}

export function ListingSort(): JSX.Element | null {
	const { sort, setSort } = useListingState();

	return (
		<SortControl
			options={LISTING_SORT_OPTIONS}
			selected={sort}
			onChange={setSort}
		/>
	);
}

export interface ListingResultsProps {
	className?: string;
	children: ReactNode;
}

export function ListingResults({
	className,
	children
}: ListingResultsProps): JSX.Element {
	const { isPending } = useListingState();

	return (
		<div
			className={className}
			aria-busy={isPending}
			data-pending={isPending || undefined}
		>
			{children}
		</div>
	);
}

/** The widest grid's first row; later cards load as the shopper scrolls. */
const EAGER_CARDS = 3;

function listingMessage({
	status,
	clearAll,
	retry,
	onward
}: ListingStateValue): ListingMessageProps {
	switch (status) {
		case "noResults":
			return {
				heading: "No products match those filters",
				body: "Try removing a filter.",
				actions: [{ label: "Clear filters", onPress: clearAll }]
			};
		case "error":
			return {
				heading: "We couldn't load products right now",
				body: "Please try again in a moment.",
				actions: [{ label: "Try again", onPress: retry }, onward]
			};
		default:
			return {
				heading: "Nothing to shop here yet",
				body: "Check back soon, or keep browsing.",
				actions: [onward]
			};
	}
}

export function ListingGrid(): JSX.Element {
	const state = useListingState();
	const { cards, firstNewCard, newCardFocused, status } = state;
	const gridRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (firstNewCard === undefined) return;
		gridRef.current
			?.querySelectorAll<HTMLElement>('[data-component="ProductCard"]')
			[firstNewCard]?.querySelector<HTMLElement>("a[href]")
			?.focus();
		newCardFocused();
	}, [firstNewCard, newCardFocused]);

	if (status !== "ready") {
		return <ListingMessage {...listingMessage(state)} />;
	}

	return (
		<div ref={gridRef}>
			<ProductGrid>
				{cards.map(({ product, statusLabel }, index) => (
					<ProductCardWithCart
						key={product.sku}
						product={
							index < EAGER_CARDS
								? product
								: {
										...product,
										image: {
											...product.image,
											loading: "lazy"
										}
									}
						}
						source="plp"
						statusLabel={statusLabel}
					/>
				))}
			</ProductGrid>
		</div>
	);
}

// A real link to the next depth, so the listing is crawled and works without script.
function nextPageHref(loaded: number): string {
	const pages = Math.ceil(loaded / LISTING_PAGE_SIZE) + 1;
	return `?${LISTING_PAGE_PARAM}=${Math.min(pages, MAX_LISTING_PAGES)}`;
}

export function ListingLoadMore(): JSX.Element | null {
	const { cards, total, loadMore, isLoadingMore, status } = useListingState();

	if (status !== "ready") return null;

	return (
		<LoadMore
			loaded={cards.length}
			total={total}
			href={nextPageHref(cards.length)}
			isLoading={isLoadingMore}
			onLoadMore={loadMore}
		/>
	);
}
